import React, { useState, useEffect, useCallback } from "react";
import { CheckInOutComponent } from "../../services/types/ComponentTypes";
import { Button } from "./Layout/Button";
import { InfoCardComponent, ButtonComponent } from "../../services/types/ComponentTypes";
import { apiService } from "../../services/api";
// import { showSuccessToast, showErrorToast } from "../Toast"; // REMOVED
import { showNotification } from "../../state/slices/notificationsSlice"; // ADDED
import { useAppSelector, useAppDispatch } from "../../state/hooks"; // ADDED useAppDispatch
import { selectUser } from "../../state/selectors";
import { checkInOutLogger } from "../../utils/checkInOutLogger";
import { useDarkMode } from "../../contexts/DarkModeContext";

interface CheckInOutProps {
    config: CheckInOutComponent;
    onActionTrigger?: (actionId: string, data?: any) => void;
}

interface CheckInOutData {
    current_state: "not_checked_in" | "checked_in" | "checked_out";
    button: {
        text: string;
        enabled: boolean;
    };
    infoCards: {
        today: string;
        checkIn: string | null;
        checkOut: string | null;
    };
    attendance_id?: string | null;
    attendance_date?: string | null;
    active_session?: {
        session_id: string;
        check_in_time: string;
    } | null;
}

/** Backend returns times as IST 'YYYY-MM-DD HH:mm:ss' or ISO. Format for display. */
function formatTimeForDisplay(isoString: string | null | undefined): string {
    if (!isoString || String(isoString).trim() === "") return "--";
    try {
        const s = String(isoString).trim();
        const formatHms12 = (hh: string, mm: string, ss: string): string => {
            const h = Number(hh);
            const m = Number(mm);
            const sec = Number(ss);
            if (
                Number.isNaN(h) ||
                Number.isNaN(m) ||
                Number.isNaN(sec) ||
                h < 0 ||
                h > 23 ||
                m < 0 ||
                m > 59 ||
                sec < 0 ||
                sec > 59
            ) {
                return "--";
            }
            const hour12 = h % 12 === 0 ? 12 : h % 12;
            const ampm = h >= 12 ? "PM" : "AM";
            return `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")} ${ampm}`;
        };

        // If backend sends "YYYY-MM-DD HH:mm:ss.ffffff" (or without fraction),
        // interpret it as UTC and convert to local time for display.
        // This matches the table summary behavior when it receives UTC/ISO values.
        const ymdTime = s.match(
            /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/
        );
        if (ymdTime) {
            const [, yy, mo, dd, hh, mm, ss, frac] = ymdTime;
            const ms = (frac ?? "").padEnd(3, "0").slice(0, 3); // microseconds -> milliseconds
            const utcMs = Date.UTC(
                Number(yy),
                Number(mo) - 1,
                Number(dd),
                Number(hh),
                Number(mm),
                Number(ss),
                Number(ms || "0")
            );
            const parsed = new Date(utcMs);
            if (!Number.isNaN(parsed.getTime())) {
                return parsed.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: true,
                });
            }
        }

        // Legacy "DD-MM-YYYY HH:mm:ss"
        const dmyTime = s.match(
            /^\d{1,2}-\d{1,2}-\d{4}\s+(\d{2}):(\d{2}):(\d{2})$/
        );
        if (dmyTime) {
            const [, hh, mm, ss] = dmyTime;
            const direct = formatHms12(hh, mm, ss);
            if (direct !== "--") return direct;
        }

        // ISO strings or any other parseable format
        const date = new Date(s);
        if (Number.isNaN(date.getTime())) return "--";
        return date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        });
    } catch {
        return "--";
    }
}

/** Current local time as display string (for optimistic check-in/out). */
function getCurrentTimeDisplay(): string {
    return formatTimeForDisplay(new Date().toISOString());
}

function formatDateForDisplay(dateString: string | null | undefined): string {
    if (!dateString) {
        return new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
        return new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
}

/**
 * Map backend response (GetCheckInOutInfo / HandleCheckInOut) to local state.
 * Backend _getCurrentState returns: infoCards.checkIn (first_check_in), infoCards.checkOut (last_check_out),
 * and active_check_in_time for current session. We show In = first/active check-in time, Out = last check-out time.
 */
function mapApiResponseToState(apiData: Record<string, any> | null): CheckInOutData {
    if (!apiData) {
        return {
            current_state: "not_checked_in",
            button: { text: "Check In", enabled: true },
            infoCards: {
                today: formatDateForDisplay(null),
                checkIn: null,
                checkOut: null,
            },
        };
    }
    const currentState = (apiData.current_state || "not_checked_in") as CheckInOutData["current_state"];
    const button = apiData.button || { text: currentState === "checked_in" ? "Check Out" : "Check In", enabled: true };

    const checkInRaw =
        apiData.infoCards?.checkIn ??
        apiData.first_check_in ??
        apiData.active_session?.check_in_time ??
        apiData.active_check_in_time;
    const checkOutRaw =
        apiData.infoCards?.checkOut ?? apiData.last_check_out;

    return {
        current_state: currentState,
        button,
        infoCards: {
            today: formatDateForDisplay(apiData.infoCards?.today ?? apiData.attendance_date),
            checkIn: checkInRaw ? formatTimeForDisplay(checkInRaw) : null,
            checkOut: checkOutRaw ? formatTimeForDisplay(checkOutRaw) : null,
        },
        attendance_id: apiData.attendance_id ?? null,
        attendance_date: apiData.attendance_date ?? null,
        active_session: apiData.active_session ?? null,
    };
}

const CheckInOut: React.FC<CheckInOutProps> = ({ config, onActionTrigger }) => {
    const user = useAppSelector(selectUser);
    const dispatch = useAppDispatch(); // ADDED
    const { isDarkMode } = useDarkMode();
    const [data, setData] = useState<CheckInOutData | null>(null);
    const [loading, setLoading] = useState<{ data: boolean; action: boolean }>({
        data: true,
        action: false,
    });
    const [error, setError] = useState<string | null>(null);

    const resolveEmployeeContext = useCallback((): { employeeId: string; branchId: string } => {
        // Prefer employee_id (employee table id). Avoid falling back to user id because
        // user id and employee id are different domains in HRMS.
        const employeeId =
            user?.employee_id ?? sessionStorage.getItem("employee_id") ?? "";
        const branchId = user?.branch_id ?? sessionStorage.getItem("branch_id") ?? "";
        return { employeeId: String(employeeId), branchId: String(branchId) };
    }, [user?.employee_id, user?.branch_id]);

    const fetchData = useCallback(async () => {
        const { employeeId } = resolveEmployeeContext();
        checkInOutLogger.info("Fetching check-in/out state", { dataApi: config.dataApi, employeeId: employeeId || "(none)" });

        setLoading(prev => ({ ...prev, data: true }));
        setError(null);

        try {
            const search: Array<{ name: string; value: string; controltype: string }> = [];
            if (employeeId && employeeId !== "000000") {
                search.push({ name: "employee_id", value: employeeId, controltype: "TextView" });
            }
            const response = await apiService.getModuleData(config.dataApi ?? "", {
                skip: 0,
                limit: 10,
                search,
                sort: [],
            });

            checkInOutLogger.info("Check-in/out state response", {
                success: response.success,
                dataLength: response.data?.length ?? 0,
            });

            if (response.success && response.data && response.data.length > 0) {
                setData(mapApiResponseToState(response.data[0]));
            } else {
                setData(mapApiResponseToState(null));
            }
        } catch (err: any) {
            checkInOutLogger.error("Failed to fetch check-in/out data", err);
            setError(err?.message ?? "Failed to load attendance data");
            setData(mapApiResponseToState(null));
        } finally {
            setLoading(prev => ({ ...prev, data: false }));
        }
    }, [config.dataApi, resolveEmployeeContext]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleAction = async () => {
        if (!data) {
            checkInOutLogger.error("Button action skipped: no data");
            return;
        }

        const previousState = data.current_state;
        const isCheckInAction = previousState === "not_checked_in" || previousState === "checked_out";
        const { employeeId, branchId } = resolveEmployeeContext();

        checkInOutLogger.info("Check-in/out button clicked", {
            action: isCheckInAction ? "check_in" : "check_out",
            current_state: data.current_state,
            attendance_id: data.attendance_id ?? undefined,
            active_session_id: data.active_session?.session_id ?? undefined,
        });

        setLoading(prev => ({ ...prev, action: true }));
        setError(null);

        try {
            const search: Array<{ name: string; value: string; controltype: string }> = [
                { name: "current_state", value: data.current_state, controltype: "Select" },
                { name: "attendance_id", value: data.attendance_id ?? "", controltype: "TextView" },
                { name: "active_session_id", value: data.active_session?.session_id ?? "", controltype: "TextView" },
            ];
            if (employeeId && employeeId !== "000000") {
                search.push({ name: "employee_id", value: employeeId, controltype: "TextView" });
            }
            if (branchId) {
                search.push({ name: "branch_id", value: branchId, controltype: "TextView" });
            }
            const response = await apiService.getModuleData(config.onPressAction ?? "", {
                skip: 0,
                limit: 10,
                search,
                sort: [],
            });

            checkInOutLogger.info("Check-in/out action response", {
                success: response.success,
                dataLength: response.data?.length ?? 0,
                message: (response as any).message,
            });

            if (response.success) {
                if (isCheckInAction) {
                    // showSuccessToast((config as any).toastMessages?.checkInSuccess ?? "Check-in successful!");
                    dispatch(showNotification({ message: (config as any).toastMessages?.checkInSuccess ?? "Check-in successful!", type: "success" }));
                } else {
                    // showSuccessToast((config as any).toastMessages?.checkOutSuccess ?? "Check-out successful!");
                    dispatch(showNotification({ message: (config as any).toastMessages?.checkOutSuccess ?? "Check-out successful!", type: "success" }));
                }

                let nextState: CheckInOutData;
                const rawRecord = Array.isArray(response.data)
                    ? response.data[0]
                    : response.data && typeof response.data === "object"
                        ? response.data
                        : null;
                if (rawRecord) {
                    nextState = mapApiResponseToState(rawRecord);
                    if (isCheckInAction && !nextState.infoCards.checkIn) {
                        nextState = {
                            ...nextState,
                            infoCards: { ...nextState.infoCards, checkIn: getCurrentTimeDisplay() },
                        };
                    }
                    if (!isCheckInAction && !nextState.infoCards.checkOut) {
                        nextState = {
                            ...nextState,
                            infoCards: { ...nextState.infoCards, checkOut: getCurrentTimeDisplay() },
                        };
                    }
                } else {
                    nextState = {
                        ...data,
                        current_state: isCheckInAction ? "checked_in" : "checked_out",
                        button: {
                            text: isCheckInAction ? "Check Out" : "Check In",
                            enabled: true,
                        },
                        infoCards: {
                            ...data.infoCards,
                            ...(isCheckInAction ? { checkIn: getCurrentTimeDisplay() } : { checkOut: getCurrentTimeDisplay() }),
                        },
                    };
                }
                setData(nextState);
            } else {
                const errorMessage = (config as any).toastMessages?.error ?? (response as any).message ?? "Action failed. Please try again.";
                // showErrorToast(errorMessage);
                dispatch(showNotification({ message: errorMessage, type: "error" }));
                setError(errorMessage);
                setTimeout(() => fetchData(), 1000);
            }
        } catch (err: any) {
            checkInOutLogger.error("Check-in/out action failed", err);
            const errorMessage = (config as any).toastMessages?.error ?? err?.message ?? "Failed to perform action. Please try again.";
            // showErrorToast(errorMessage);
            dispatch(showNotification({ message: errorMessage, type: "error" }));
            setError(errorMessage);
            setTimeout(() => fetchData(), 1000);
        } finally {
            setLoading(prev => ({ ...prev, action: false }));
        }
    };


    if (loading.data && !data) {
        return (
            <div style={{
                backgroundColor: isDarkMode ? "#1e293b" : (config.style?.backgroundColor || "hsl(var(--bg-white))"),
                padding: config.style?.padding || 8, // var(--space-2)
                margin: config.style?.margin || 16, // var(--space-4)
                borderRadius: config.style?.borderRadius || 4, // var(--radius)
                textAlign: "center",
                color: isDarkMode ? "#94a3b8" : "hsl(var(--text-gray-500))",
                fontSize: "var(--text-sm)"
            }}>
                Loading attendance data...
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{
                backgroundColor: isDarkMode ? "#1e293b" : (config.style?.backgroundColor || "hsl(var(--bg-white))"),
                padding: config.style?.padding || 8, // var(--space-2)
                margin: config.style?.margin || 16, // var(--space-4)
                borderRadius: config.style?.borderRadius || 4, // var(--radius)
                textAlign: "center",
                color: isDarkMode ? "#f87171" : "#EF4444",
                fontSize: "var(--text-sm)"
            }}>
                {error || "Failed to load attendance data"}
            </div>
        );
    }

    // Helper function to parse columnNumber and return responsive grid CSS variables
    const getResponsiveGridVars = (
        columnNumber: string | undefined
    ): React.CSSProperties => {
        const defaultColumns = "m1 t3 w6";
        const cols = columnNumber || defaultColumns;
        const [mobile, tablet, web] = cols
            .split(" ")
            .map((col: string) => parseInt(col.substring(1)));

        return {
            "--mobile-cols": mobile,
            "--tablet-cols": tablet,
            "--desktop-cols": web,
        } as React.CSSProperties;
    };

    // Container styles - Match summary stats section alignment
    // Extract style to avoid margin override
    const { margin, ...restStyle } = (config.style || {}) as React.CSSProperties;

    const containerStyle: React.CSSProperties = {
        backgroundColor: "transparent",
        padding: config.style?.padding ?? 0,
        borderRadius: config.style?.borderRadius ?? 12,
        border: "none",
        width: "100%",
        boxSizing: "border-box",
        ...restStyle,
        // Keep it tight inside the attendance panel
        marginTop: 8,
        marginBottom: 8,
        marginLeft: 0,
        marginRight: 0,
    };

    // Single strip style to avoid "too many cards/lines"
    const stripStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: 0,
        padding: "10px 12px",
        borderRadius: 12,
        backgroundColor: isDarkMode ? "rgba(15, 23, 42, 0.55)" : "rgba(249, 250, 251, 1)",
        border: "none",
        width: "100%",
        minWidth: 0,
        boxShadow: isDarkMode ? "0 1px 2px rgba(0,0,0,0.24)" : "0 1px 2px rgba(16,24,40,0.06)",
    };

    const stripItemStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "0 10px",
        whiteSpace: "nowrap",
        minWidth: 0,
        flex: "0 0 auto",
    };

    const stripDividerStyle: React.CSSProperties = { display: "none" };
    const cardLabelColor = isDarkMode ? "#94a3b8" : "#727272";
    const cardValueColor = isDarkMode ? "#f1f5f9" : "hsl(var(--text-gray-900))";
    const cardValuePrimary = isDarkMode ? "#93c5fd" : "hsl(var(--color-primary))";
    const cardValueOrange = isDarkMode ? "#fdba74" : "#F97316";

    // Flexbox styles - Info cards on left, buttons on right
    const gridStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
        backgroundColor: "transparent",
        width: "100%",
    };

    // Create InfoCard configs with improved width
    const todayInfoCard: InfoCardComponent = {
        componentType: "infoCard" as any,
        name: "Today",
        value: data.infoCards.today,
        icon: "⏰",
        style: {
            backgroundColor: "#ffffff",
            padding: 6, // Increased from 4 to 6
            borderRadius: 4, // var(--radius-sm)
            textColor: "hsl(var(--text-gray-900))",
            fontSize: 11, // Close to var(--text-xs)
        } as any, // Allow minWidth in style
    };

    const checkInInfoCard: InfoCardComponent = {
        componentType: "infoCard" as any,
        name: "In",
        value: data.infoCards.checkIn || "--",
        icon: "→",
        style: {
            backgroundColor: "#ffffff", // Light primary
            padding: 6, // Increased from 4 to 6
            borderRadius: 4, // var(--radius-sm)
            textColor: "hsl(var(--color-primary))",
            fontSize: 11, // Close to var(--text-xs)
        } as any,
    };

    const checkOutInfoCard: InfoCardComponent = {
        componentType: "infoCard" as any,
        name: "Out",
        value: data.infoCards.checkOut || "--",
        icon: "←",
        style: {
            backgroundColor: "#ffffff", // Light orange
            padding: 6, // Increased from 4 to 6
            borderRadius: 4, // var(--radius-sm)
            textColor: "#F97316", // Orange accent
            fontSize: 11, // Close to var(--text-xs)
        } as any,
    };

    // Create Button config
    const actionButton: ButtonComponent = {
        componentType: "button" as any,
        name: data.button.text,
        onPressAction: config.onPressAction, // Set the action ID
        disabled: !data.button.enabled || loading.action,
        loading: loading.action,
        style: {
            // Semantic colors: green for "Check In", red for "Check Out"
            // (when current_state === "checked_in", the next action is typically "Check Out")
            backgroundColor: data.current_state === "checked_in" ? "#EF4444" : "#22C55E",
            textColor: "hsl(var(--color-primary-foreground))",
            padding: "10px 12px",
            borderRadius: 10,
            fontWeight: "700",
            fontSize: 12,
        } as any,
    };

    // Create Log Button config
    const logButton: ButtonComponent | null = config.logButtonAction ? {
        componentType: "button" as any,
        name: "Log",
        onPressAction: config.logButtonAction,
        disabled: !data?.attendance_id,
        style: {
            backgroundColor: isDarkMode ? "#111827" : "#ffffff",
            textColor: isDarkMode ? "#e5e7eb" : "#111827",
            padding: "10px 12px",
            borderRadius: 10,
            fontWeight: "700",
            fontSize: 12,
            borderWidth: 1,
            borderColor: isDarkMode ? "#334155" : "#E5E7EB",
        } as any,
    } : null;

    return (
        <div style={containerStyle}>
            <style>{`
        .checkinout-grid {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          width: 100%;
        }
        
        .checkinout-info-cards {
          display: flex;
          flex-direction: row;
          gap: 10px;
          flex: 1 1 auto;
          min-width: 0;
          flex-wrap: wrap;
        }
        
        .checkinout-buttons {
          display: flex;
          flex-direction: row;
          gap: 10px;
          flex: 0 0 auto;
          margin-left: auto;
        }
        
        @media (max-width: 1024px) {
          .checkinout-grid {
            flex-wrap: wrap;
          }
          
          .checkinout-buttons {
            margin-left: 0;
            width: 100%;
            justify-content: flex-end;
          }
        }
        
        @media (max-width: 768px) {
          .checkinout-grid {
            flex-direction: column;
            align-items: stretch;
          }
          
          .checkinout-info-cards {
            flex-direction: row;
            width: 100%;
          }
          
          .checkinout-buttons {
            width: 100%;
            justify-content: flex-end;
          }
        }
      `}</style>

            {error && (
                <div style={{
                    padding: "var(--space-2)", // 8px
                    marginBottom: "var(--space-2)", // 8px
                    backgroundColor: isDarkMode ? "rgba(220, 38, 38, 0.2)" : "hsl(0 84% 97%)",
                    color: isDarkMode ? "#f87171" : "#DC2626",
                    borderRadius: "var(--radius-sm)", // 4px
                    fontSize: "var(--text-xs)", // 12px
                }}>
                    {error}
                </div>
            )}

            <div className="checkinout-grid" style={gridStyle}>
                {/* Info strip - Grouped on the left */}
                <div className="checkinout-info-cards">
                    <div style={stripStyle}>
                        <div style={{ ...stripItemStyle, paddingLeft: 2 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                <span style={{ fontSize: 14, color: isDarkMode ? "#94a3b8" : "#6B7280" }}>⏰</span>
                                <span style={{ fontSize: 11, color: cardLabelColor, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                    Today
                                </span>
                            </div>
                            <span style={{ fontSize: 14, color: cardValueColor, fontWeight: 800 }}>
                                {data.infoCards.today}
                            </span>
                        </div>

                        <div style={stripDividerStyle} />

                        <div style={stripItemStyle}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                <span style={{ fontSize: 14, color: cardValuePrimary }}>→</span>
                                <span style={{ fontSize: 11, color: cardLabelColor, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                    In
                                </span>
                            </div>
                            <span style={{ fontSize: 14, color: cardValuePrimary, fontWeight: 800 }}>
                                {data.infoCards.checkIn || "--"}
                            </span>
                        </div>

                        <div style={stripDividerStyle} />

                        <div style={{ ...stripItemStyle, paddingRight: 2 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                <span style={{ fontSize: 14, color: cardValueOrange }}>←</span>
                                <span style={{ fontSize: 11, color: cardLabelColor, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                    Out
                                </span>
                            </div>
                            <span style={{ fontSize: 14, color: cardValueOrange, fontWeight: 800 }}>
                                {data.infoCards.checkOut || "--"}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Buttons - Grouped on the right */}
                <div className="checkinout-buttons">
                    <Button
                        config={actionButton}
                        onClick={() => handleAction()}
                    />

                    {logButton && (
                        <Button
                            config={logButton}
                            onClick={(actionId) => {
                                if (onActionTrigger) {
                                    // Pass the data including attendance_date
                                    onActionTrigger(actionId, {
                                        attendance_id: data?.attendance_id,
                                        attendance_date: data?.attendance_date,
                                        ...data
                                    });
                                }
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default CheckInOut;
